import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
// import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'; // Removed unused import
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface ExpenseFormData {
  category: string;
  subcategory: string;
  amount: number;
  date: string;
  description: string;
  receiptUrl?: string;
}

const EXPENSE_CATEGORIES = [
  {
    name: 'Зарплаты',
    subcategories: ['Офис', 'Монтажники', 'Менеджеры', 'Бухгалтерия', 'Другое']
  },
  {
    name: 'Транспорт',
    subcategories: ['Бензин', 'Техобслуживание', 'Ремонт', 'Страховка', 'Аренда', 'Другое']
  },
  {
    name: 'Аренда',
    subcategories: ['Офис', 'Склад', 'Земля под конструкции', 'Оборудование', 'Другое']
  },
  {
    name: 'Материалы',
    subcategories: ['LED материалы', 'Печатные материалы', 'Конструкции', 'Инструменты', 'Другое']
  },
  {
    name: 'Коммунальные услуги',
    subcategories: ['Электричество', 'Вода', 'Отопление', 'Интернет', 'Телефон', 'Другое']
  },
  {
    name: 'Налоги',
    subcategories: ['НДС', 'Подоходный', 'Социальный', 'Имущественный', 'Другое']
  },
  {
    name: 'Маркетинг',
    subcategories: ['Реклама', 'Мероприятия', 'Сайт', 'Соцсети', 'Другое']
  },
  {
    name: 'Командировки',
    subcategories: ['Транспорт', 'Проживание', 'Питание', 'Другое']
  },
  {
    name: 'Офис',
    subcategories: ['Канцтовары', 'Мебель', 'Техника', 'Программное обеспечение', 'Расходные материалы', 'Другое']
  },
  {
    name: 'Юридические',
    subcategories: ['Консультации', 'Регистрационные сборы', 'Лицензии', 'Разрешения', 'Другое']
  },
  {
    name: 'Другое',
    subcategories: ['Другое']
  }
];

const ExpenseForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth(); // Changed from isAllowed
  
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableSubcategories, setAvailableSubcategories] = useState<string[]>([]);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState<string>('');
  
  // Form setup
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<ExpenseFormData>({
    defaultValues: {
      category: EXPENSE_CATEGORIES[0].name,
      subcategory: EXPENSE_CATEGORIES[0].subcategories[0],
      date: new Date().toISOString().split('T')[0],
      amount: 0,
      description: ''
    }
  });
  
  // Watch category for subcategory updates
  const watchCategory = watch('category');
  
  // Update subcategories when category changes
  useEffect(() => {
    const selectedCategory = EXPENSE_CATEGORIES.find(c => c.name === watchCategory);
    if (selectedCategory) {
      setAvailableSubcategories(selectedCategory.subcategories);
      // Set default subcategory when category changes
      setValue('subcategory', selectedCategory.subcategories[0]);
    }
  }, [watchCategory, setValue]);
  
  // Load expense data for edit mode
  useEffect(() => {
    const loadExpenseData = async () => {
      if (!isEditMode) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const response = await axios.get(`/expenses/${id}`);
        const expenseData = response.data;
        
        // Set form values
        setValue('category', expenseData.category);
        setValue('subcategory', expenseData.subcategory);
        setValue('amount', expenseData.amount);
        setValue('date', expenseData.date.split('T')[0]);
        setValue('description', expenseData.description);
        
        if (expenseData.receiptUrl) {
          setReceiptUrl(expenseData.receiptUrl);
        }
      } catch (err) {
        console.error('Error loading expense data:', err);
        setError('Ошибка при загрузке данных о расходе');
      } finally {
        setLoading(false);
      }
    };
    
    loadExpenseData();
  }, [id, isEditMode, setValue]);
  
  // Handle form submission
  const onSubmit = async (data: ExpenseFormData) => {
    setSubmitting(true);
    setError(null);
    
    try {
      // First, upload receipt if present
      let uploadedReceiptUrl = receiptUrl;
      
      if (receiptFile) {
        setUploadingReceipt(true);
        try {
          const formData = new FormData();
          formData.append('receipt', receiptFile);
          
          const uploadResponse = await axios.post('/uploads/receipts', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          uploadedReceiptUrl = uploadResponse.data.url;
        } catch (err) {
          console.error('Error uploading receipt:', err);
          setError('Ошибка при загрузке чека');
          setSubmitting(false);
          setUploadingReceipt(false);
          return;
        }
        setUploadingReceipt(false);
      }
      
      // Prepare data with receipt URL
      const expenseData = {
        ...data,
        receiptUrl: uploadedReceiptUrl
      };
      
      // Save expense data
      if (isEditMode) {
        await axios.put(`/expenses/${id}`, expenseData);
      } else {
        await axios.post('/expenses', expenseData);
      }
      
      navigate('/expenses');
    } catch (err) {
      console.error('Error saving expense:', err);
      setError('Ошибка при сохранении расхода');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle receipt file selection
  const handleReceiptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReceiptFile(file);
    }
  };
  
  // Removed unused formatCurrency function
  
  // Check permissions
  if (isEditMode ? !can('expenses.edit') : !can('expenses.create')) { // Changed from isAllowed
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">У вас нет прав доступа к этой странице</p>
      </div>
    );
  }
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title={isEditMode ? 'Редактировать расход' : 'Добавить расход'}
        backLink="/expenses"
      />
      
      <div className="bg-white rounded-lg shadow p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Category */}
            <div>
              <label htmlFor="category" className="form-label">Категория *</label>
              <select
                id="category"
                className={`form-input ${errors.category ? 'border-red-500' : ''}`}
                {...register('category', { required: 'Категория обязательна' })}
              >
                {EXPENSE_CATEGORIES.map(category => (
                  <option key={category.name} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
              )}
            </div>
            
            {/* Subcategory */}
            <div>
              <label htmlFor="subcategory" className="form-label">Подкатегория *</label>
              <select
                id="subcategory"
                className={`form-input ${errors.subcategory ? 'border-red-500' : ''}`}
                {...register('subcategory', { required: 'Подкатегория обязательна' })}
              >
                {availableSubcategories.map(subcategory => (
                  <option key={subcategory} value={subcategory}>
                    {subcategory}
                  </option>
                ))}
              </select>
              {errors.subcategory && (
                <p className="mt-1 text-sm text-red-600">{errors.subcategory.message}</p>
              )}
            </div>
            
            {/* Amount */}
            <div>
              <label htmlFor="amount" className="form-label">Сумма *</label>
              <input
                id="amount"
                type="number"
                min="0"
                step="1"
                className={`form-input ${errors.amount ? 'border-red-500' : ''}`}
                placeholder="Введите сумму расхода"
                {...register('amount', { 
                  required: 'Сумма обязательна',
                  min: { value: 1, message: 'Сумма должна быть больше 0' },
                  valueAsNumber: true
                })}
              />
              {errors.amount && (
                <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
              )}
            </div>
            
            {/* Date */}
            <div>
              <label htmlFor="date" className="form-label">Дата *</label>
              <input
                id="date"
                type="date"
                className={`form-input ${errors.date ? 'border-red-500' : ''}`}
                {...register('date', { required: 'Дата обязательна' })}
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
              )}
            </div>
          </div>
          
          {/* Description */}
          <div>
            <label htmlFor="description" className="form-label">Описание *</label>
            <textarea
              id="description"
              rows={3}
              className={`form-input ${errors.description ? 'border-red-500' : ''}`}
              placeholder="Введите описание расхода"
              {...register('description', { 
                required: 'Описание обязательно',
                minLength: { value: 5, message: 'Описание должно быть не менее 5 символов' }
              })}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>
          
          {/* Receipt Upload */}
          <div>
            <label htmlFor="receipt" className="form-label">Чек или квитанция</label>
            
            {receiptUrl && (
              <div className="mb-3 p-3 bg-gray-50 rounded flex items-center justify-between">
                <a 
                  href={receiptUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark"
                >
                  Просмотреть загруженный чек
                </a>
              </div>
            )}
            
            <div className="mt-2">
              <input
                id="receipt"
                type="file"
                accept="image/*,application/pdf"
                onChange={handleReceiptChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary file:text-white
                  hover:file:bg-primary-dark"
              />
              <p className="mt-1 text-xs text-gray-500">
                Поддерживаемые форматы: JPG, PNG, PDF. Максимальный размер: 5MB
              </p>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/expenses')}
              className="btn-outline"
              disabled={submitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  {uploadingReceipt ? 'Загрузка чека...' : 'Сохранение...'}
                </>
              ) : (
                isEditMode ? 'Обновить' : 'Создать'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseForm;
